import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

/**
 * API configuration constants
 */
const API_CONFIG = {
  BASE_URL: '/parts-and-shipments',
  TIMEOUT: 10 * 1000, // 10 seconds
  ERROR_MESSAGES: {
    CREATE_ERROR: 'Unable to create part.',
    UPDATE_ERROR: 'Unable to update part information.',
    DELETE_ERROR: 'Unable to delete part.',
    GENERIC_ERROR: 'Unable to process part.',
    NETWORK_ERROR: 'Network error occurred. Please check your connection.',
  },
} as const;

/**
 * Generic object type for API responses
 */
interface ApiResponse<T = unknown> {
  [key: string]: T;
}

/**
 * Custom error class for API-related errors
 */
class ApiError extends Error {
  public readonly statusCode?: number;

  public readonly originalError?: unknown;

  constructor(message: string, statusCode?: number, originalError?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.originalError = originalError;
  }
}

/**
 * Creates and returns a configured Axios instance for parts and shipments API
 * @returns Configured Axios instance
 */
function getPartsAndShipmentsApiInstance(): AxiosInstance {
  return axios.create({
    baseURL: API_CONFIG.BASE_URL,
    timeout: API_CONFIG.TIMEOUT,
    timeoutErrorMessage: API_CONFIG.ERROR_MESSAGES.GENERIC_ERROR,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-User-Id': window?.context?.id || 0,
      'X-Group-Id': window?.context?.group?.id || 0,
      'X-Role-Id': window?.context?.group?.acting_role_id || 0,
      'X-Company-Id': window?.context?.company?.id || 0,
    },
  });
}

/**
 * Handles and transforms various error types into ApiError instances
 * @param error - The caught error
 * @returns Formatted ApiError instance
 */
function handleApiError(error: any): ApiError {
  if (error instanceof ApiError) {
    return error;
  }

  if (axios.isAxiosError(error)) {
    if (error?.response?.data) {
      const data = error.response.data as any;
      if (data.errors && Array.isArray(data.errors)) {
        const message = data.errors
          .map((e: any) => (typeof e === 'string' ? e : e.message || JSON.stringify(e)))
          .join(', ');
        return new ApiError(message, error.response.status, error);
      }
    }

    const method = error.config?.method?.toUpperCase();
    let errorMessage : string = API_CONFIG.ERROR_MESSAGES.GENERIC_ERROR;
    if (method === 'POST') {
      errorMessage = API_CONFIG.ERROR_MESSAGES.CREATE_ERROR;
    } else if (method === 'PUT' || method === 'PATCH') {
      errorMessage = API_CONFIG.ERROR_MESSAGES.UPDATE_ERROR;
    } else if (method === 'DELETE') {
      errorMessage = API_CONFIG.ERROR_MESSAGES.DELETE_ERROR;
    }

    return new ApiError(errorMessage, error.response?.status || 400, error);
  }

  return new ApiError(API_CONFIG.ERROR_MESSAGES.GENERIC_ERROR, 400, error);
}

/**
 * Makes an API request with proper error handling and type safety
 * @param options - Axios request configuration
 * @returns Promise resolving to the API response data
 * @throws ApiError when the request fails
 */
export default async function makePartsAndShipmentsApiRequest<T = ApiResponse>(
  options: AxiosRequestConfig,
): Promise<T> {
  try {
    const apiInstance = getPartsAndShipmentsApiInstance();
    const response = await apiInstance.request<T>(options);

    if (!response.data) {
      throw new ApiError(
        API_CONFIG.ERROR_MESSAGES.GENERIC_ERROR,
        response.status,
        response,
      );
    }

    return response.data;
  } catch (error) {
    throw handleApiError(error);
  }
}
